import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import { fetchExternalApiLimits, updateExternalApiLimit, type ExternalApiLimit } from './api'

const QUERY_KEY = ['external-api-limits']

/**
 * Administrator-managed limits for the External API's five-minute granular
 * endpoint (backend core/services/external_api_limits.py). Saved values are
 * checked server-side against each limit's safe range, audited, and picked
 * up by every portal worker without a redeploy.
 */
export default function ExternalApiLimitsSection() {
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: QUERY_KEY, queryFn: fetchExternalApiLimits })
  if (isLoading) return <LoadingState label="Loading API limits…" />
  if (isError) {
    return <ErrorState message={error instanceof Error ? error.message : 'Failed to load API limits.'} onRetry={() => refetch()} />
  }
  return (
    <section className="mt-10">
      <h2 className="mb-1 text-xl font-bold text-ink">External API Limits</h2>
      <p className="mb-4 text-sm text-text-secondary">
        Limits for the five-minute granular data endpoint. Changes apply to every portal worker within seconds and are recorded
        with who changed what.
      </p>
      <div className="space-y-3">
        {(data ?? []).map((limit) => (
          <LimitRow key={limit.key} limit={limit} />
        ))}
      </div>
    </section>
  )
}

function LimitRow({ limit }: { limit: ExternalApiLimit }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(String(limit.value))
  const mutation = useMutation({
    mutationFn: () => updateExternalApiLimit(limit.key, limit.choices ? draft : Number(draft)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
  const changed = draft !== String(limit.value)
  const range = limit.choices ? `One of: ${limit.choices.join(', ')}` : `${limit.minimum}–${limit.maximum} ${limit.unit}`
  const inputId = `limit-${limit.key}`
  return (
    <div className="rounded-2xl border border-nav-line bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="text-sm font-semibold text-ink">
            {limit.label}
          </label>
          {limit.description && <p className="mt-0.5 text-xs text-text-secondary">{limit.description}</p>}
          <p className="mt-1 text-xs text-text-tertiary">
            Allowed: {range}. Default: {String(limit.default)}
            {limit.updated_by ? ` · last changed by ${limit.updated_by}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {limit.choices ? (
            <select
              id={inputId}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="rounded-xl border border-nav-line bg-input px-3 py-1.5 text-sm text-text-primary"
            >
              {limit.choices.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={inputId}
              type="number"
              inputMode="numeric"
              min={limit.minimum ?? undefined}
              max={limit.maximum ?? undefined}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-32 rounded-xl border border-nav-line bg-input px-3 py-1.5 text-right text-sm text-text-primary"
            />
          )}
          <button
            type="button"
            disabled={!changed || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-full bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {mutation.isError && (
        <p className="mt-2 text-sm text-error" role="alert">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed to save.'}
        </p>
      )}
    </div>
  )
}
